/**
 * FocusedScreenFrame: the display half of the focused client screens that live outside the tab
 * shell (booking detail, About, the sent form), shared by the Mini App and the studio website
 * (shared-client-screens D2). Browser-only rails: the `client-lock` harden, the open-at-the-top
 * scroll, and the floating FlowBackButton; the container's and the back button's positioning stay
 * with the consumer via `className` / `backClassName` (each page's fixed hero geometry).
 *
 * The Mini App's FocusedScreenShell wraps it with its Telegram parts (the safe-area vars and the
 * mount-scoped `data-client-theme`); the website writes its theme on the server.
 *
 * The frame's loading and message states are components here, never its stylesheet object, so
 * the website draws them without reaching into the app's styles (Guard 1).
 */
import { useLayoutEffect, type ReactNode } from 'react';
import { useClientLock } from '../../hooks/useClientLock';
import { FlowBackButton } from './FlowBackButton';
import styles from './FocusedScreenShell.module.css';

export interface FocusedScreenFrameProps {
  children: ReactNode;
  /** Back-button handler; absent, no back button (the website's guest home is the front door). */
  onBack?: () => void;
  /** Consumer-owned container styling (position/background/overflow). */
  className?: string;
  /** Consumer-owned back-button positioning (fixed + safe-area offsets). */
  backClassName?: string;
  /** Container test id (lets a consumer's existing tests keep their hook). */
  testId?: string;
}

export function FocusedScreenFrame({ children, onBack, className, backClassName, testId }: FocusedScreenFrameProps) {
  useClientLock();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={className} data-testid={testId}>
      {onBack && <FlowBackButton className={[styles.back, backClassName].filter(Boolean).join(' ')} onClick={onBack} />}
      {children}
    </div>
  );
}

/** The frame's centered spinner while the screen's read loads. */
export function FocusedScreenLoading({ testId }: { testId?: string }) {
  return (
    <div className={styles.stateWrap} data-testid={testId}>
      <div className={styles.spinner} />
    </div>
  );
}

export interface FocusedScreenMessageProps {
  title: string;
  message: string;
  action: { label: string; onClick: () => void };
  testId?: string;
}

/** The frame's centered message with one action (Try again, Back). */
export function FocusedScreenMessage({ title, message, action, testId }: FocusedScreenMessageProps) {
  return (
    <div className={styles.stateWrap} data-testid={testId}>
      <div className={styles.stateTitle}>{title}</div>
      <p>{message}</p>
      <button type="button" className={styles.stateBtn} onClick={action.onClick}>
        {action.label}
      </button>
    </div>
  );
}
